import React, { useState, useEffect } from "react";
import {
  Input,
  Form,
  Row,
  Col,
  Switch,
  Typography,
  Button,
  Modal,
  message,
  Card,
  Divider,
  Table,
  InputNumber,
  Image,
} from "antd";
import VariantForm from "../../../Products/components/variantForm";
import { productTypes } from "../../../../types";
import ExtrasForm from "../../../Products/components/extrasModal";
import { useQuery } from "@apollo/client";
import { GET_PRODUCTS } from "./requests";

const { Title, Text } = Typography;

interface FormProductProps {
  isCreateView: boolean;
  setIsSaveProductData: (value: boolean) => void;
  imageUrl: string | null;
  totalPrice: number | null;
  comparePrice?: number | null;
  setPayload: (payload: any) => void;
  product?: typeof productTypes.product | null;
  refetch?: () => void;
}

const FormProduct: React.FC<FormProductProps> = ({
  isCreateView,
  setIsSaveProductData,
  setPayload,
  totalPrice,
  comparePrice,
  imageUrl,
  product,
  refetch,
}) => {
  const [form] = Form.useForm();
  const [descriptionProduct, setDescriptionProduct] = useState<string | null>(null);
  const [productVariants, setProductVariants] = useState<typeof productTypes.productVariant[] | null>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [hasVariants, setHasVariants] = useState<boolean>(false);
  const [localConfirmText, setLocalConfirmText] = useState<string>("");
  const [loadingSwitch, setLoadingSwitch] = useState(false);
  const [hasExtras, setHasExtras] = useState<boolean | undefined>(false);
  const [selectedProductsExtra, setSelectedProductsExtra] = useState<{
    product: string;
    max: number;
    required: boolean;
  }[]>([]);
  const [requireBeverages, setRequireBeverages] = useState<boolean | undefined>(false);
  const [requireComplements, setRequireComplements] = useState<boolean | undefined>(false);
  const [hasBeverages, setHasBeverages] = useState<boolean | undefined>(false);
  const [hasComplements, setHasComplements] = useState<boolean | undefined>(false);
  const [maxBeverages, setMaxBeverages] = useState<number | undefined>(0);
  const [maxComplements, setMaxComplements] = useState<number | undefined>(0);
  const [selectedComplements, setSelectedComplements] = useState<string[]>([]);
  const [selectedBeverages, setSelectedBeverages] = useState<string[]>([]);
  const [pageConfigBeverages, setPageConfigBeverages] = useState({ page: 1,
    pageSize: 5 });
  const [pageConfig, setPageConfig] = useState({ page: 1,
    pageSize: 5 });

  useEffect(() => {
    if (product) {
      const newInitialValues = {
        name: product?.name || "",
        description: product?.description || "",
        type: product?.type || "",
        img: product?.variants?.[0]?.img || "",
        price: product?.variants?.length === 1 ? product?.variants[0].price : undefined,
        variants:
              product.variants?.length > 1
                ? product?.variants.map((variant) => ({
                  id: variant.id,
                  title: variant.title || "",
                  price: variant.price,
                  description: variant.description || "",
                }))
                : [],
      };

      setProductVariants(product.variants.map(variant => ({
        id: variant.id,
        title: variant.title || "",
        price: variant.price || 0,
        img: variant.img,
        description: variant.description || "",
        isPrimaryVariant: variant.isPrimaryVariant,
      })));

      setHasVariants(product.variants.length > 1);
      setRequireComplements(product.rulesAdditionals?.requireComplements);
      setHasComplements(product.rulesAdditionals?.complementItems ? product.rulesAdditionals?.complementItems.length > 0 : false);
      setMaxComplements(product.rulesAdditionals?.maxComplements);
      setRequireBeverages(product.rulesAdditionals?.requireBeverages);
      setHasBeverages(product.rulesAdditionals?.beverageItems ? product.rulesAdditionals?.beverageItems.length > 0 : false);
      setMaxBeverages(product.rulesAdditionals?.maxBeverages);
      setHasExtras(product.rulesAdditionals?.includesExtras)
      setSelectedProductsExtra(
        product.rulesAdditionals?.extraRules
          ? product.rulesAdditionals.extraRules.map((item) => ({
            product: item.product.id,
            max: item.max ?? 0,
            required: item.required ?? false,
          }))
          : [],
      );
      setDescriptionProduct(product.description)

      setSelectedBeverages(
        product.rulesAdditionals?.beverageItems && product.rulesAdditionals?.beverageItems.length > 0 ?
          product.rulesAdditionals.beverageItems.map((item) => item.id) : [],
      );

      setSelectedComplements(
        product.rulesAdditionals?.complementItems && product.rulesAdditionals?.complementItems.length > 0 ?
          product.rulesAdditionals.complementItems.map((item) => item.id) : [],
      );

      form.setFieldsValue(newInitialValues);
    }
  }, [product, form]);

  useEffect(()=>{
    setIsSaveProductData(false)
  }, [
    descriptionProduct,
    requireBeverages,
    requireComplements,
    hasExtras,
    selectedBeverages,
    selectedComplements,
    selectedProductsExtra,
    maxBeverages,
    maxComplements,
    hasBeverages,
    hasComplements,
  ])

  const { data: productsBeverages, loading: loadingProductsBeverages } = useQuery(GET_PRODUCTS, {
    variables: {
      params: pageConfigBeverages,
      type: "beverage",
    },
  });

  const { data: productsComplements, loading: loadingProductsComplements } = useQuery(GET_PRODUCTS, {
    variables: {
      params: pageConfig,
      type: "complement",
    },
  });

  const showConfirm = () => {
    setIsModalVisible(true);
  };

  const handleOk = () => {
    desactiveVariants();
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setLocalConfirmText("");
  };

  const desactiveVariants = async () => {
    try {
      setLoadingSwitch(true);
      setHasVariants(false);

      message.success("Variantes desactivadas exitosamente.");
      setProductVariants([]);
    } catch(err) {
      console.log(err)
    } finally {
      setLocalConfirmText("");
      setLoadingSwitch(false);
    }
  };

  const rowSelectionComplements = {
    selectedRowKeys: selectedComplements,
    onChange: (selectedRowKeys: React.Key[]) => {
      const validSelectedRowKeys = selectedRowKeys.filter(key => key !== null);
      setSelectedComplements(validSelectedRowKeys as string[]);
    },
    preserveSelectedRowKeys: true,
  };

  const rowSelectionBeverages = {
    selectedRowKeys: selectedBeverages,
    onChange: (selectedRowKeys: React.Key[]) => {
      const validSelectedRowKeys = selectedRowKeys.filter(key => key !== null);
      setSelectedBeverages(validSelectedRowKeys as string[]);
    },
    preserveSelectedRowKeys: true,
  };

  const handleSelectComplements = (record: typeof productTypes.product) => {
    const selectedProductIds = [...selectedComplements];
    const productId = record.id;

    if (selectedProductIds.includes(productId)) {
      setSelectedComplements(selectedProductIds.filter(id => id !== productId));
    } else {
      setSelectedComplements([...selectedProductIds, productId]);
    }
  };

  const handleSelectBeverages = (record: typeof productTypes.product) => {
    const selectedProductIds = [...selectedBeverages];
    const productId = record.id;

    if (selectedProductIds.includes(productId)) {
      setSelectedBeverages(selectedProductIds.filter(id => id !== productId));
    } else {
      setSelectedBeverages([...selectedProductIds, productId]);
    }
  };

  const columns = [
    {
      title: "Imagen",
      key: "img",
      render: (row: typeof productTypes.productVariant) => {
        return (
          <Image
            width={50}
            src={row.img}
            alt={row.title}
          />
        );
      },
    },
    {
      title: "Nombre",
      key: "name",
      render: (row: typeof productTypes.productVariant) => {
        if(row.title){
          return <Text strong>{row.title}</Text>;
        } else {
          return <Text strong>{row?.product?.name}</Text>;
        }
      },
    },
    {
      title: "Descripción",
      key: "description",
      render: (row: typeof productTypes.productVariant) => {
        if(row.description){
          return <Text strong>{row.description}</Text>;
        } else {
          return <Text strong>{row?.product?.description}</Text>;
        }
      },
    },
  ];

  const savePayload = () => {
    const updatedProductVariants = productVariants?.map((variant) => {
      const variantCopy = { ...variant };
      delete variantCopy.id;
      return variantCopy;
    });

    const payload: any = {
      product: {
        name: descriptionProduct,
        description: descriptionProduct,
        categories: [],
        type: "coupon",
        rulesAdditionals: {
          requireComplements: requireComplements,
          maxComplements: maxComplements,
          complementItems: selectedComplements,
          requireBeverages: requireBeverages,
          maxBeverages: maxBeverages,
          beverageItems: selectedBeverages,
          includesExtras: hasExtras,
          extraRules: selectedProductsExtra,
        },
      },
      ...(isCreateView && {
        productVariants: hasVariants
          ? updatedProductVariants
          : {
            price: Number(totalPrice),
            comparePrice: comparePrice ? Number(comparePrice) : null,
            img: imageUrl,
          },
      }),
    };

    setPayload(payload);
    setIsSaveProductData(true);
  };

  return (
    <Card
      style={{
        boxShadow: "rgba(0, 0, 0, 0.15) 1.95px 1.95px 2.6px",
        borderRadius: "15px",
      }}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="Descripción del Producto"
        >
          <Input
            placeholder="Ingrese la descripción del producto"
            value={descriptionProduct ?? ""}
            onChange={(e) => setDescriptionProduct(e.target.value)}
          />
        </Form.Item>
      </Form>
      <Row align="middle" justify="space-between">
        <Col>
          <Title level={4}>Agregar Variantes</Title>
        </Col>
        <Col>
          <Switch checked={hasVariants} onChange={(checked) => {
            if (!checked) {
              showConfirm();
            } else {
              setHasVariants(true);
            }
          }} />
        </Col>
      </Row>
      {hasVariants && (
        <VariantForm
          product={product ?? null}
          variants={productVariants}
          setVariants={setProductVariants}
          isCreateView={isCreateView}
          refetch={refetch}
        />
      )}
      <Divider />
      <Col span={24}>
        <Row style={{ justifyContent: "space-between" }}>
          <Title level={4}>Agregar Extras</Title>
          <Switch checked={hasExtras} onChange={setHasExtras} />
        </Row>
      </Col>
      {hasExtras && (
        <ExtrasForm
          selectedProducts={selectedProductsExtra}
          setSelectedProducts={setSelectedProductsExtra}
        />
      )}
      <Divider />
      <Row align="middle" justify="space-between">
        <Col>
          <Title level={4}>Agregar Complementos</Title>
        </Col>
        <Col>
          <Switch checked={hasComplements} onChange={setHasComplements} />
        </Col>
      </Row>

      {hasComplements && (
        <>
          <Title level={5}>Máximo de Complementos</Title>
          <Row>
            <InputNumber min={0} value={maxComplements} onChange={(value) => setMaxComplements(value ?? 0)} />
            <Text style={{ marginLeft: 10,
              marginRight: 5 }} strong>¿Obligatorio?</Text>
            <Switch checked={requireComplements} onChange={setRequireComplements} />
          </Row>

          <Table
            rowSelection={rowSelectionComplements}
            onRow={(record: typeof productTypes.product) => ({
              onClick: () => handleSelectComplements(record),
            })}
            rowKey="id"
            columns={columns}
            dataSource={productsComplements?.productVariants.results ?? []}
            loading={loadingProductsComplements}
            pagination={{
              current: Number(pageConfig.page),
              defaultCurrent: pageConfig.page,
              pageSize: Number(pageConfig.pageSize),
              defaultPageSize: pageConfig.pageSize,
              total: productsComplements?.productVariants?.info?.count,
              showTotal: (total) => <Text disabled>{`${total} resultados`}</Text>,
              onChange: (page, pageSize) => {
                setPageConfig({ page,
                  pageSize });
              },
              showSizeChanger: true,
            }}
          />
        </>
      )}

      <Row align="middle" justify="space-between">
        <Col>
          <Title level={4}>Agregar Bebidas</Title>
        </Col>
        <Col>
          <Switch checked={hasBeverages} onChange={setHasBeverages} />
        </Col>
      </Row>

      {hasBeverages && (
        <>
          <Title level={5}>Máximo de Bebidas</Title>
          <Row>
            <InputNumber min={0} value={maxBeverages} onChange={(value) => setMaxBeverages(value ?? 0)} />
            <Text style={{ marginLeft: 10,
              marginRight: 5 }} strong>¿Obligatorio?</Text>
            <Switch checked={requireBeverages} onChange={setRequireBeverages} />
          </Row>

          <Table
            rowSelection={rowSelectionBeverages}
            onRow={(record: typeof productTypes.product) => ({
              onClick: () => handleSelectBeverages(record),
            })}
            rowKey="id"
            columns={columns}
            loading={loadingProductsBeverages}
            dataSource={productsBeverages?.productVariants.results ?? []}
            pagination={{
              current: Number(pageConfigBeverages.page),
              defaultCurrent: pageConfigBeverages.page,
              pageSize: Number(pageConfigBeverages.pageSize),
              defaultPageSize: pageConfigBeverages.pageSize,
              total: productsBeverages?.productVariants?.info?.count,
              showTotal: (total) => <Text disabled>{`${total} resultados`}</Text>,
              onChange: (page, pageSize) => {
                setPageConfigBeverages({ page,
                  pageSize });
              },
              showSizeChanger: true,
            }}
          />
        </>
      )}

      <Button
        type="primary"
        style={{ width: "100%" }}
        onClick={savePayload}
        disabled={
          (hasVariants && !productVariants?.length) ||
          (hasExtras && selectedProductsExtra.length === 0) ||
          (hasBeverages && selectedBeverages.length === 0) ||
          (hasComplements && selectedComplements.length === 0) ||
          !descriptionProduct
        }
      >
        Guardar Datos del Producto
      </Button>

      <Modal
        title="¿Estás seguro de desactivar las variantes?"
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancelar
          </Button>,
          <Button
            key="ok"
            type="primary"
            disabled={localConfirmText !== "CONTINUAR"}
            onClick={handleOk}
            loading={loadingSwitch}
          >
            Confirmar
          </Button>,
        ]}
      >
        <p>Si desactivas las variantes, todas serán eliminadas excepto la variante principal. Esta acción no se puede deshacer.</p>
        <p>Para confirmar, escribe <strong>CONTINUAR</strong> en el siguiente campo:</p>
        <Input
          placeholder="Escribe CONTINUAR"
          value={localConfirmText}
          onChange={(e) => {
            setLocalConfirmText(e.target.value);
          }}
        />
      </Modal>
    </Card>
  );
};

export default FormProduct;
